import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactGA from 'react-ga4';
import SEO from '../../components/SEO';

// Styled Components
const LandingContainer = styled.div`
  font-family: 'Lato', sans-serif;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  background: linear-gradient(to bottom, #fff6f8 0%, #fff 100%);
  padding: 5rem 0;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 3rem 0;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  max-width: 600px;
  
  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const EmotionalBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: rgba(255, 170, 138, 0.2);
  color: #FFAA8A;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
`;

const Heading = styled.h1`
  font-size: 3.2rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: #2D3142;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  span {
    color: var(--primary);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 8px;
      background-color: rgba(233, 64, 87, 0.2);
      z-index: -1;
    }
  }
`;

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: #6C757D;
  margin-bottom: 2rem;
  line-height: 1.6;
  font-style: italic;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background-color: var(--primary);
  color: white;
  font-weight: 700;
  padding: 1rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(233, 64, 87, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(233, 64, 87, 0.4);
    color: white;
  }
`;

const DiscountTag = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: #FFAA8A;
  color: #2D3142;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  transform: rotate(10deg);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  font-size: 0.9rem;
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;
`;

const HeroImageContainer = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    margin-top: 3rem;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
`;

const QuoteOverlay = styled.div`
  position: absolute;
  bottom: -30px;
  right: -30px;
  background-color: white;
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  max-width: 250px;
  
  @media (max-width: 768px) {
    position: relative;
    bottom: 0;
    right: 0;
    margin: -50px auto 0;
    max-width: 80%;
  }
`;

const QuoteText = styled.p`
  font-style: italic;
  color: #6C757D;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  line-height: 1.5;
`;

const QuoteAuthor = styled.p`
  font-weight: 700;
  color: #2D3142;
  margin-bottom: 0;
  font-size: 0.9rem;
`;

const TransformationSection = styled.section`
  padding: 5rem 0;
  background-color: white;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #2D3142;
  text-align: center;
  
  span {
    color: var(--primary);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 8px;
      background-color: rgba(233, 64, 87, 0.2);
      z-index: -1;
    }
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #6C757D;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  font-style: italic;
`;

const TransformationCard = styled.div`
  background-color: white;
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  height: 100%;
  border-top: 5px solid ${props => props.color || 'var(--primary)'};
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const BeforeAfterHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const BeforeAfterBadge = styled.div`
  background-color: ${props => props.color || 'rgba(233, 64, 87, 0.1)'};
  color: ${props => props.textColor || 'var(--primary)'};
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 0.9rem;
`;

const TransformationTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2D3142;
`;

const TransformationDescription = styled.p`
  color: #6C757D;
  margin-bottom: 0;
  line-height: 1.6;
`;

const TestimonialSection = styled.section`
  padding: 5rem 0;
  background-color: #f8f9fa;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('/images/dots-pattern.svg');
    opacity: 0.1;
    z-index: 1;
  }
`;

const TestimonialContainer = styled.div`
  position: relative;
  z-index: 2;
`;

const TestimonialCard = styled.div`
  background-color: white;
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  height: 100%;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const TestimonialQuote = styled.div`
  position: relative;
  padding-left: 2rem;
  margin-bottom: 1.5rem;
  
  &::before {
    content: '"';
    position: absolute;
    top: -20px;
    left: 0;
    font-size: 4rem;
    color: rgba(233, 64, 87, 0.2);
    font-family: Georgia, serif;
    line-height: 1;
  }
`;

const TestimonialText = styled.p`
  font-size: 1.1rem;
  color: #6C757D;
  line-height: 1.6;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const TestimonialAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  i {
    font-size: 1.5rem;
    color: #adb5bd;
  }
`;

const TestimonialInfo = styled.div``;

const TestimonialName = styled.h5`
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: #2D3142;
`;

const TestimonialRole = styled.p`
  font-size: 0.9rem;
  color: #adb5bd;
  margin-bottom: 0;
`;

const EmotionalBenefitsSection = styled.section`
  padding: 5rem 0;
  background-color: white;
`;

const BenefitCard = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const BenefitIcon = styled.div`
  width: 80px;
  height: 80px;
  background-color: rgba(233, 64, 87, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  
  i {
    font-size: 2rem;
    color: var(--primary);
  }
`;

const BenefitTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2D3142;
`;

const BenefitDescription = styled.p`
  color: #6C757D;
  margin-bottom: 0;
  line-height: 1.6;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
`;

const CTASection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(135deg, #E94057 0%, #F27121 100%);
  color: white;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

const CTAButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: white;
  color: var(--primary);
  font-weight: 700;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
    color: var(--primary);
  }
`;

const LandingPageEmotional = () => {
  // SEO metadata based on the SEO strategy document
  const seoData = {
    title: "Transform Your Family's Sleep Journey | Emotional Sleep Support",
    description: "From exhaustion to joy - discover how our personalized sleep plans can transform your family's emotional wellbeing. Get the peaceful nights your family deserves.",
    keywords: [
      "baby sleep emotional support", 
      "gentle sleep training", 
      "family sleep wellbeing",
      "peaceful sleep solutions",
      "sleep deprivation help",
      "emotional sleep journey",
      "parent sleep support",
      "family-centered sleep plans"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Dozzy Emotional Sleep Support",
      "description": "Personalized sleep plans focused on emotional wellbeing for the whole family",
      "provider": {
        "@type": "Organization",
        "name": "Dozzy.au"
      },
      "offers": {
        "@type": "Offer",
        "price": "49.00",
        "priceCurrency": "AUD"
      }
    }
  };

  // Google Analytics tracking code setup
  useEffect(() => {
    // Initialize Google Analytics with the correct tracking ID
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-CTM5672S7L';
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', 'G-CTM5672S7L', {
      page_title: 'Emotional Landing Page',
      page_path: '/landing-emotional',
      campaign_source: 'instagram',
      campaign_medium: 'social',
      campaign_name: 'emotional_journey'
    });

    // Event tracking for CTA clicks
    const trackCTAClick = () => {
      gtag('event', 'cta_click', {
        'event_category': 'engagement',
        'event_label': 'Emotional Landing Page CTA',
        'value': 1
      });
    };

    // Add event listeners to CTA buttons
    const ctaButtons = document.querySelectorAll('.cta-button');
    ctaButtons.forEach(button => {
      button.addEventListener('click', trackCTAClick);
    });

    return () => {
      // Clean up event listeners
      ctaButtons.forEach(button => {
        button.removeEventListener('click', trackCTAClick);
      });
    };
  }, []);

  return (
    <LandingContainer>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={seoData.structuredData}
        canonicalUrl="https://dozzy.au/landing-emotional"
        ogType="website"
      />
      <HeroSection>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <HeroContent>
                <EmotionalBadge>
                  <i className="bi bi-heart-fill me-2"></i>
                  Stories from Parents
                </EmotionalBadge>
                <Heading>
                  From Sleepless Nights to <span>Better Sleep</span>
                </Heading>
                <SubHeading>
                  "We've helped parents work toward better sleep habits and support their family's wellbeing using Artificial Intelligence (AI) technology."
                </SubHeading>
                <ButtonWrapper>
                  <CtaButton to="/signup" className="cta-button">
                    Start Your Sleep Transformation
                  </CtaButton>
                </ButtonWrapper>
                
                <div className="d-flex align-items-center flex-wrap">
                  <div className="me-4 mb-3">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-star-fill text-warning me-1"></i>
                      <i className="bi bi-star-fill text-warning me-1"></i>
                      <i className="bi bi-star-fill text-warning me-1"></i>
                      <i className="bi bi-star-fill text-warning me-1"></i>
                      <i className="bi bi-star-fill text-warning"></i>
                    </div>
                    <p className="mb-0 mt-1" style={{ color: '#6C757D' }}>
                      <small>Positive feedback from parents</small>
                    </p>
                  </div>
                </div>
              </HeroContent>
            </div>
            <div className="col-lg-6">
              <HeroImageContainer>
                <HeroImage 
                  src="/images/jonathan-borba-RWgE9_lKj_Y-unsplash.jpg" 
                  alt="Peaceful sleeping baby" 
                />
                <QuoteOverlay>
                  <QuoteText>
                    "After 8 months of broken sleep, our daughter now sleeps through the night. It's like we have our lives back!"
                  </QuoteText>
                  <QuoteAuthor>
                    — Sarah M., Melbourne
                  </QuoteAuthor>
                </QuoteOverlay>
              </HeroImageContainer>
            </div>
          </div>
        </div>
      </HeroSection>

      <TransformationSection>
        <div className="container">
          <SectionTitle>
            The <span>Transformation</span> Journey
          </SectionTitle>
          <SectionSubtitle>
            See how Dozzy has changed the lives of families just like yours
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-md-6">
              <TransformationCard color="rgba(233, 64, 87, 0.7)">
                <BeforeAfterHeader>
                  <BeforeAfterBadge color="rgba(233, 64, 87, 0.1)" textColor="var(--primary)">
                    BEFORE DOZZY
                  </BeforeAfterBadge>
                </BeforeAfterHeader>
                <TransformationTitle>Exhaustion & Frustration</TransformationTitle>
                <TransformationDescription>
                  "I was waking up 5-6 times every night with my 10-month-old. I tried everything—white noise, different swaddles, earlier bedtimes—nothing worked. I was so exhausted I could barely function at work. My relationship with my partner was suffering because we were both so sleep-deprived and irritable."
                </TransformationDescription>
              </TransformationCard>
            </div>
            
            <div className="col-md-6">
              <TransformationCard color="rgba(126, 203, 185, 0.7)">
                <BeforeAfterHeader>
                  <BeforeAfterBadge color="rgba(126, 203, 185, 0.1)" textColor="var(--secondary)">
                    AFTER DOZZY
                  </BeforeAfterBadge>
                </BeforeAfterHeader>
                <TransformationTitle>Restful Nights & Joy</TransformationTitle>
                <TransformationDescription>
                  "After implementing our Dozzy sleep plan, I feel more confident in my approach to my daughter's sleep. The personalized guidance helped me establish a routine that works for our family. While we still have occasional challenging nights, I now have strategies that help us all get better rest."
                </TransformationDescription>
              </TransformationCard>
            </div>
          </div>
          
          <div className="row mt-4">
            <div className="col-md-6">
              <TransformationCard color="rgba(233, 64, 87, 0.7)">
                <BeforeAfterHeader>
                  <BeforeAfterBadge color="rgba(233, 64, 87, 0.1)" textColor="var(--primary)">
                    BEFORE DOZZY
                  </BeforeAfterBadge>
                </BeforeAfterHeader>
                <TransformationTitle>Anxiety & Uncertainty</TransformationTitle>
                <TransformationDescription>
                  "I was constantly second-guessing myself about my daughter's sleep. Was I creating bad habits? Was I responding too quickly or not quickly enough? Every night felt like a battle, and I'd lie awake worrying even when she was sleeping. I felt like I was failing as a mother."
                </TransformationDescription>
              </TransformationCard>
            </div>
            
            <div className="col-md-6">
              <TransformationCard color="rgba(126, 203, 185, 0.7)">
                <BeforeAfterHeader>
                  <BeforeAfterBadge color="rgba(126, 203, 185, 0.1)" textColor="var(--secondary)">
                    AFTER DOZZY
                  </BeforeAfterBadge>
                </BeforeAfterHeader>
                <TransformationTitle>Confidence & Peace</TransformationTitle>
                <TransformationDescription>
                  "Our Dozzy plan gave me helpful guidance on what might work for my family. The step-by-step approach helped establish a bedtime routine that my daughter seems to respond well to. While we still have challenges, I feel more confident in my approach to her sleep needs."
                </TransformationDescription>
              </TransformationCard>
            </div>
          </div>
        </div>
      </TransformationSection>

      <TestimonialSection>
        <TestimonialContainer className="container">
          <SectionTitle>
            Hear From <span>Parents Like You</span>
          </SectionTitle>
          <SectionSubtitle>
            Real stories from families who've experienced the Dozzy difference
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-md-4">
              <TestimonialCard>
                <TestimonialQuote>
                  <TestimonialText>
                    I was struggling after months of broken sleep. Dozzy's plan offered a gentle approach that aligned with my parenting style. My son's sleep has improved, and I'm feeling more rested as a result.
                  </TestimonialText>
                </TestimonialQuote>
                <TestimonialAuthor>
                  
                  <TestimonialInfo>
                    <TestimonialName>Jessica T.</TestimonialName>
                    <TestimonialRole>Mother of 14-month-old</TestimonialRole>
                  </TestimonialInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            </div>
            
            <div className="col-md-4">
              <TestimonialCard>
                <TestimonialQuote>
                  <TestimonialText>
                    As a single dad, I was struggling with bedtime challenges. Dozzy gave me a structured routine that complemented my parenting style. Bedtime has become a more positive experience for both of us.
                  </TestimonialText>
                </TestimonialQuote>
                <TestimonialAuthor>
                  
                  <TestimonialInfo>
                    <TestimonialName>Michael R.</TestimonialName>
                    <TestimonialRole>Father of 2-year-old</TestimonialRole>
                  </TestimonialInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            </div>
            
            <div className="col-md-4">
              <TestimonialCard>
                <TestimonialQuote>
                  <TestimonialText>
                    I was anxious about sleep training and kept delaying it. Dozzy's approach aligned with my comfort level and parenting philosophy. The supportive guidance in the plan was helpful for our family's journey.
                  </TestimonialText>
                </TestimonialQuote>
                <TestimonialAuthor>
                  
                  <TestimonialInfo>
                    <TestimonialName>Emma L.</TestimonialName>
                    <TestimonialRole>Mother of 8-month-old</TestimonialRole>
                  </TestimonialInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            </div>
          </div>
        </TestimonialContainer>
      </TestimonialSection>

      <EmotionalBenefitsSection>
        <div className="container">
          <SectionTitle>
            Beyond Better <span>Sleep</span>
          </SectionTitle>
          <SectionSubtitle>
            Better sleep habits may contribute to your family's overall wellbeing
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-md-4">
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-heart"></i>
                </BenefitIcon>
                <BenefitTitle>Stronger Relationships</BenefitTitle>
                <BenefitDescription>
                  Better sleep may help you have more energy for nurturing connections with your child, partner, and friends.
                </BenefitDescription>
              </BenefitCard>
            </div>
            
            <div className="col-md-4">
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-emoji-smile"></i>
                </BenefitIcon>
                <BenefitTitle>Improved Mental Health</BenefitTitle>
                <BenefitDescription>
                  Research suggests that improved sleep patterns may help reduce feelings of stress and support better mental wellbeing.
                </BenefitDescription>
              </BenefitCard>
            </div>
            
            <div className="col-md-4">
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-battery-charging"></i>
                </BenefitIcon>
                <BenefitTitle>Renewed Energy</BenefitTitle>
                <BenefitDescription>
                  Better sleep patterns may help you feel more rested to engage with daily parenting activities.
                </BenefitDescription>
              </BenefitCard>
            </div>
          </div>
          
          <div className="row mt-4">
            <div className="col-md-4">
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-graph-up"></i>
                </BenefitIcon>
                <BenefitTitle>Better Development</BenefitTitle>
                <BenefitDescription>
                  Research indicates that quality sleep is an important factor in supporting your child's development and overall wellbeing.
                </BenefitDescription>
              </BenefitCard>
            </div>
            
            <div className="col-md-4">
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-calendar-check"></i>
                </BenefitIcon>
                <BenefitTitle>Predictable Routines</BenefitTitle>
                <BenefitDescription>
                  Establish healthy sleep habits that create security and predictability for your child.
                </BenefitDescription>
              </BenefitCard>
            </div>
            
            <div className="col-md-4">
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-house-heart"></i>
                </BenefitIcon>
                <BenefitTitle>Peaceful Home</BenefitTitle>
                <BenefitDescription>
                  Work toward creating a more restful home environment with improved sleep habits for the whole family.
                </BenefitDescription>
              </BenefitCard>
            </div>
          </div>
        </div>
      </EmotionalBenefitsSection>

      <CTASection>
        <div className="container">
          <CTATitle style={{ color: 'white' }}>
            Your Family's Sleep Journey Starts Today
          </CTATitle>
          <CTADescription>
            Join Australian families seeking better sleep with Dozzy's personalized sleep plans powered by Artificial Intelligence.
          </CTADescription>
          <CTAButtonWrapper>
            <CTAButton to="/signup" className="cta-button">
              Transform Your Sleep Now
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </CTASection>
    </LandingContainer>
  );
};

export default LandingPageEmotional;
